import {
  DraftConfidence,
  DraftRunStage,
  DraftRunStatus,
  IDraftAttribution,
  IDraftRecMatch,
  IDraftRecommendation,
  IWorkerProgress,
} from "store/queries/contractModelDrafts";

import { CONTRACT_MODEL, modelIdLabel } from "./copy";
import { describeRuleLogic, ruleAppliesTo } from "./wizard/ruleSummary";

/** Upload types clear-api accepts (mirrors ALLOWED_EXTENSIONS in the backend service). */
export const ALLOWED_UPLOAD_EXTENSIONS = [
  "pdf",
  "xlsx",
  "xls",
  "csv",
  "docx",
  "doc",
  "txt",
];

/** Display label for a run: its name, or a short id fallback. */
export const runDisplayName = (run: {
  name: string | null;
  id: string;
}): string => run.name?.trim() || modelIdLabel(run.id);

/** Format a run timestamp for the list table. */
export const formatRunDate = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

/** Statuses where the model is being produced (worker claimed / running / returned, awaiting persist). */
export const PROCESSING_STATUSES: DraftRunStatus[] = [
  "queued",
  "running",
  "worker_returned",
];

export const isProcessing = (status: DraftRunStatus): boolean =>
  PROCESSING_STATUSES.includes(status);

/**
 * Statuses a run may be canceled from — mirrors `CANCELABLE_STATUSES` in clear-api. Not while the worker
 * holds it (`running`, `worker_returned`: the server refuses with 409), and not once terminal.
 */
export const CANCELABLE_STATUSES: DraftRunStatus[] = [
  "collecting",
  "queued",
  "awaiting_input",
  "completed",
  "failed",
];

export const canCancelRun = (status: DraftRunStatus): boolean =>
  CANCELABLE_STATUSES.includes(status);

/**
 * Whether to OFFER "Delete run": a canceled run with no recommendations. A client-side hint only — the
 * server also refuses anything materialized, reviewed, implemented or compared on ANY version (409).
 */
export const canDeleteRun = (run: {
  status: DraftRunStatus;
  recommendationCount: number;
}): boolean => run.status === "canceled" && run.recommendationCount === 0;

/** Friendly, human-facing label for a run status. */
export const runStatusLabel = (status: DraftRunStatus): string => {
  switch (status) {
    case "collecting":
      return "Draft";
    case "queued":
    case "running":
    case "worker_returned":
      return "Generating";
    case "awaiting_input":
      return "Needs input";
    case "completed":
      return "Completed";
    case "failed":
    case "dead":
      return "Failed";
    case "canceled":
      return "Canceled";
    default:
      return status;
  }
};

/**
 * Helper text shown next to the Generate button, or null when it should just be enabled.
 * Ordered by precedence: the reason the button is disabled *right now* wins.
 */
export const generateHint = (
  documentCount: number,
  status: DraftRunStatus,
  opts: { uploading?: boolean; finished?: boolean } = {}
): string | null => {
  if (opts.uploading) return "Wait for the upload to finish before generating.";
  // A finished run is no longer a dead end: documents can be added and the model re-derived into a new
  // version, with the reviewer's verdicts carried forward. Saying "start a new run" would send her off
  // to throw away work she doesn't need to lose.
  if (opts.finished)
    return "Already generated. Add a document or an instruction, then Regenerate — your confirmations are kept.";
  if (documentCount === 0) return "Upload at least one document to generate.";
  if (isProcessing(status))
    return "The model is analyzing your documents — this can take a few minutes.";
  return null;
};

/**
 * The generation pipeline, as actual stages rather than a decorative bar.
 *
 * These are real: a run moves `queued` → `running` → `worker_returned` as the agents worker claims it,
 * extracts the documents and runs the model, and then as clear-api parses and persists what came back.
 * Because the status genuinely reports where the work is, we can tell the reviewer what is happening
 * instead of an indefinite "working on it" — which, on a job that takes several minutes, reads as
 * "possibly broken".
 */
export interface IGenerationStage {
  status: DraftRunStatus;
  label: string;
  detail: string;
}

/** CLE-2811 — the queued step's live text. The worker drains the queue every few seconds. */
export const QUEUED_PROGRESS_TEXT =
  "Waiting for the worker — it usually picks up within 10 seconds.";

/**
 * CLE-2811 — the running step's subtitle before the worker reports a phase (the first second, or a worker
 * build without live progress). A facts pass pulls out the contract facts; a full pass models the rates.
 */
const RUNNING_DETAIL_BY_STAGE: Record<DraftRunStage, string> = {
  facts:
    "Extracting the contract text, then pulling out the contract facts: dates, timely filing, escalators, implants and the reimbursement schedule.",
  full: "Extracting the contract text, then working through it against the modeling playbook.",
};

export const runningStepDetail = (stage: DraftRunStage): string =>
  RUNNING_DETAIL_BY_STAGE[stage] ?? RUNNING_DETAIL_BY_STAGE.full;

export const GENERATION_STAGES: IGenerationStage[] = [
  {
    status: "queued",
    label: "Queued",
    detail: QUEUED_PROGRESS_TEXT,
  },
  {
    status: "running",
    label: "Reading and modeling the contract",
    // The full-stage text; the page shows `runningStepDetail(stage)` in its place (see describeProgress).
    detail: runningStepDetail("full"),
  },
  {
    status: "worker_returned",
    label: "Saving recommendations",
    detail: `Checking the AI's output and writing it to the ${CONTRACT_MODEL}.`,
  },
];

/** Which stage a status is on, or -1 when the run isn't generating. */
export const generationStageIndex = (status: DraftRunStatus): number =>
  GENERATION_STAGES.findIndex((s) => s.status === status);

/**
 * Coarse elapsed time — "4m", "1h 12m". Deliberately not seconds-precise: this sits next to a job
 * measured in minutes, and a ticking seconds counter invites staring at it.
 */
export const formatElapsed = (fromIso: string, nowMs: number): string => {
  const started = new Date(fromIso).getTime();
  if (Number.isNaN(started)) return "";
  const mins = Math.floor(Math.max(0, nowMs - started) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
};

/**
 * CLE-2811 — the `detail` the worker sends on the one `extracting` state of a run that reuses the
 * previous pass's text instead of downloading and extracting again. Must match
 * REUSED_EXTRACTION_DETAIL in agents/scripts/contract-model-worker.mjs.
 */
export const REUSED_EXTRACTION_DETAIL = "reusing text from the previous pass";

/** "Taking longer than usual" once the elapsed time passes this multiple of the typical duration. */
export const LONGER_THAN_USUAL_FACTOR = 2;

/**
 * A live counter: "45 s", "2 min 5 s", "1 h 3 min". Whole seconds, never negative (the worker's and the
 * browser's clocks can disagree by a little).
 */
export const formatCounter = (ms: number): string => {
  const total = Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 1000)) : 0;
  if (total < 60) return `${total} s`;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  if (mins < 60) return secs ? `${mins} min ${secs} s` : `${mins} min`;
  return `${Math.floor(mins / 60)} h ${mins % 60} min`;
};

/**
 * "about 45 seconds" under a minute; otherwise minutes to the nearest half — "about 1 minute",
 * "about 1½ minutes", "about 12 minutes". Null when there is no typical duration yet.
 */
export const formatTypicalDuration = (ms: number | null): string | null => {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return null;
  const seconds = Math.max(1, Math.round(ms / 1000));
  if (seconds < 60)
    return `about ${seconds} ${seconds === 1 ? "second" : "seconds"}`;
  const halves = Math.round(ms / 30000);
  const whole = Math.floor(halves / 2);
  const half = halves % 2 === 1;
  return `about ${whole}${half ? "½" : ""} ${
    whole === 1 && !half ? "minute" : "minutes"
  }`;
};

/**
 * How long the current generation has been going. The clock starts at the worker's claim
 * (`startedAt`); while still `queued`, `startedAt` belongs to the PREVIOUS generation, so the enqueue
 * (the run's last update) is used instead. Null when the timestamp cannot be read.
 */
export const generationElapsedMs = (
  run: { status: DraftRunStatus; startedAt: string | null; updatedAt: string },
  nowMs: number
): number | null => {
  const from =
    run.status === "queued" || !run.startedAt ? run.updatedAt : run.startedAt;
  const startMs = Date.parse(from);
  return Number.isNaN(startMs) ? null : Math.max(0, nowMs - startMs);
};

/** True once the run has taken more than LONGER_THAN_USUAL_FACTOR times the typical duration. */
export const isTakingLongerThanUsual = (
  elapsedMs: number | null,
  typicalMs: number | null
): boolean =>
  elapsedMs != null &&
  typicalMs != null &&
  typicalMs > 0 &&
  elapsedMs > LONGER_THAN_USUAL_FACTOR * typicalMs;

/** The line above the steps: "Usually about 1½ minutes · 45 s so far". Null when there is neither. */
export const durationSummary = (
  elapsedMs: number | null,
  typicalMs: number | null
): string | null => {
  const typical = formatTypicalDuration(typicalMs);
  const soFar = elapsedMs == null ? null : `${formatCounter(elapsedMs)} so far`;
  if (typical && soFar) return `Usually ${typical} · ${soFar}`;
  if (typical) return `Usually ${typical}`;
  return soFar;
};

/**
 * CLE-2811 — what the run is doing right now, in words: the current step's subtitle. Built from the
 * worker's live progress while `running`; the status alone says enough while `queued` or
 * `worker_returned`. A running run with no progress yet (the first second, or a worker build without
 * live progress) falls back to the step's generic text for its `stage`.
 */
export const describeProgress = (
  status: DraftRunStatus,
  progress: IWorkerProgress | null,
  nowMs: number,
  stage: DraftRunStage = "full"
): string => {
  if (status === "queued") return QUEUED_PROGRESS_TEXT;
  if (status === "worker_returned") return "Saving recommendations";
  const fallback = runningStepDetail(stage);
  if (!progress) return fallback;
  switch (progress.phase) {
    case "downloading":
      return progress.current != null && progress.total != null
        ? `Downloading document ${progress.current} of ${progress.total}`
        : "Downloading the documents";
    case "extracting": {
      if (progress.detail === REUSED_EXTRACTION_DETAIL)
        return "Reusing the text from the previous pass";
      let text = progress.detail
        ? `Reading ${progress.detail}`
        : "Reading the documents";
      if (progress.current != null && progress.total)
        text += `, page ${progress.current} of ${progress.total}`;
      if (progress.ocrPages)
        text += `, ${progress.ocrPages} scanned ${
          progress.ocrPages === 1 ? "page" : "pages"
        } OCR'd so far`;
      return text;
    }
    case "thinking": {
      const started = Date.parse(
        progress.thinkingStartedAt ?? progress.phaseStartedAt ?? ""
      );
      const model = progress.model ? ` (${progress.model})` : "";
      const counter = Number.isNaN(started)
        ? ""
        : ` — ${formatCounter(nowMs - started)}`;
      return `The AI is working through the contract${model}${counter}`;
    }
    case "depositing":
      return "Saving recommendations";
    default:
      return fallback;
  }
};

/** PrimeReact Tag severity for a run status. */
export const runStatusSeverity = (
  status: DraftRunStatus
): "success" | "info" | "warning" | "danger" | undefined => {
  switch (status) {
    case "completed":
      return "success";
    case "queued":
    case "running":
    case "worker_returned":
      return "info";
    case "awaiting_input":
      return "warning";
    case "failed":
    case "dead":
      return "danger";
    default:
      return undefined; // collecting, canceled → neutral
  }
};

/** Map a confidence bucket to a PrimeReact Tag severity. */
export const confidenceSeverity = (
  c: DraftConfidence
): "success" | "warning" | "danger" => {
  if (c === "High") return "success";
  if (c === "Medium") return "warning";
  return "danger";
};

/** Group recommendations by componentType for sectioned display. */
/** The order a human would actually build these in — files/terms first, then rates, then rulesets. */
const IMPLEMENTATION_ORDER: string[] = [
  "contract_term",
  "base_rate_file",
  "rate_calculation",
  "carveout",
  "case_rate_ruleset",
  "package_ruleset",
  "inpatient_ruleset",
  "exploding_fee_schedule",
  "mpdr_ruleset",
  "lesser_of_charges",
  "charge_based_pricing",
  "ma_payer_link",
  "provider_decomposition",
  "payer_consolidation",
];

/** Recommendations ordered by build sequence (then sortOrder) — files/terms first, then rates, then rulesets. */
export const forChecklist = (
  recs: IDraftRecommendation[]
): IDraftRecommendation[] => {
  const rank = (t: string) => {
    const i = IMPLEMENTATION_ORDER.indexOf(t);
    return i === -1 ? IMPLEMENTATION_ORDER.length : i;
  };
  return [...recs].sort(
    (a, b) =>
      rank(a.componentType) - rank(b.componentType) || a.sortOrder - b.sortOrder
  );
};

export const groupByComponentType = (
  recs: IDraftRecommendation[]
): Record<string, IDraftRecommendation[]> => {
  // Group in the same build order the implementation checklist uses (terms/files first, then rates,
  // then rulesets) so the recommendation cards read top-to-bottom the way a human would build them,
  // not in generation order. Component types are non-numeric keys, so insertion order is preserved.
  const byType: Record<string, IDraftRecommendation[]> = {};
  forChecklist(recs).forEach((r) => {
    byType[r.componentType] = byType[r.componentType] || [];
    byType[r.componentType].push(r);
  });
  return byType;
};

/** Coerce a model-supplied string array (missingInputs / openQuestions) into clean strings. */
const stringList = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.map((m) => String(m).trim()).filter((m) => m.length > 0)
    : [];

/** Advisory items a human should confirm. These never block a run. */
export const openQuestionsOf = (rec: IDraftRecommendation): string[] =>
  stringList(rec.openQuestions);

/** BLOCKERS: inputs without which this component cannot be modeled at all. */
export const missingInputsOf = (rec: IDraftRecommendation): string[] =>
  stringList(rec.missingInputs);

/** Every distinct thing the model said it still needs, across the whole run. */
export const collectMissingInputs = (
  recs: IDraftRecommendation[]
): string[] => {
  const seen = new Set<string>();
  recs.forEach((rec) => missingInputsOf(rec).forEach((m) => seen.add(m)));
  return [...seen];
};

// Heuristic: does this missing input read like a document the user should upload?
const DOCUMENT_HINTS = [
  "agreement",
  "amendment",
  "exhibit",
  "addendum",
  "attachment",
  "schedule",
  "document",
  "file",
  "not included",
  "not provided",
  "upload",
];

export const looksLikeMissingDocument = (input: string): boolean => {
  const s = input.toLowerCase();
  return DOCUMENT_HINTS.some((hint) => s.includes(hint));
};

/**
 * A run is "finished" once it has produced recommendations. Re-generating or adding documents then
 * would append a second set of recommendations to the same version, so those actions are locked.
 */
export const isRunFinished = (recs: IDraftRecommendation[]): boolean =>
  recs.length > 0;

/** A recommendation is unverified-sourced if it has attributions but none verified. */
export const hasUnverifiedAttribution = (rec: IDraftRecommendation): boolean =>
  rec.attributions.length > 0 && rec.attributions.every((a) => !a.verified);

/**
 * How much a source quote is actually worth.
 *
 * `verified` means the quote was matched against text WE extracted from the file. A quote that merely
 * matched the model's own reported chunk proves self-consistency, not that the span is in the contract
 * — so it gets its own, deliberately unreassuring badge rather than the green one.
 */
/**
 * What a "verified" quote means, and — more importantly — what an unverified one means.
 *
 * We extract every document to text ourselves (pdftotext, OCR for scans, exceljs for workbooks) BEFORE
 * the model reads it. A quote is `verified` only when it matches text WE extracted. That is independent
 * evidence the sentence is really in the contract.
 *
 * "Not verified" does NOT mean the quote is wrong — it means we could not confirm it. The usual causes
 * are OCR reading a scanned page differently, or the model paraphrasing rather than quoting. But it also
 * cannot rule out that the model made the sentence up, which is exactly why it is called out.
 *
 * The old label leaked the matching method ("fuzzy_ws") into the UI, which meant nothing to anyone.
 */
export const attributionBadge = (
  a: Pick<IDraftAttribution, "verified" | "verificationMethod">
): { label: string; icon: string; color: string; tip: string } => {
  if (a.verified)
    return {
      label: "Found in the document",
      icon: "pi-check-circle",
      color: "var(--green-600)",
      tip: "We found this exact sentence in the text we extracted from the source file — independent evidence it is really in the contract.",
    };
  return {
    label: "Not auto-verified",
    icon: "pi-info-circle",
    color: "var(--text-color-secondary)",
    tip: "We couldn't automatically match this quote against the text we extracted — common for scanned pages and spreadsheets, where formatting differs. It doesn't mean the quote is wrong; check it against the source to be sure.",
  };
};

/** Human labels for the componentType enum — the snake_case values read as too technical in the UI. */
const COMPONENT_TYPE_LABELS: Record<string, string> = {
  provider_decomposition: "Provider Decomposition",
  payer_consolidation: "Payer Consolidation",
  contract_term: "Contract Term",
  base_rate_file: "Base Rate File",
  rate_calculation: "Rate Calculation",
  carveout: "Carveout",
  case_rate_ruleset: "Case-Rate Ruleset",
  inpatient_ruleset: "Inpatient Ruleset",
  package_ruleset: "Package Ruleset",
  exploding_fee_schedule: "Exploding Fee Schedule",
  mpdr_ruleset: "Multiple-Procedure Discount",
  lesser_of_charges: "Lesser-of-Charges",
  charge_based_pricing: "Charge-Based Pricing",
  ma_payer_link: "Medicare Advantage Link",
};

export const componentTypeLabel = (type: string): string =>
  COMPONENT_TYPE_LABELS[type] ?? type.replace(/_/g, " ");

/** Human label for the rate tier a run is scoped to. */
export const siteTierLabel = (tier: string | null): string | null => {
  if (tier === "non_facility") return "Office / Non-Facility";
  if (tier === "facility") return "Facility / Outpatient";
  if (tier === "both") return "Both tiers";
  return null;
};

/** Turn a proposedModeling key ("ratePercent", "cptCode") into a readable label ("Rate Percent", "CPT Code"). */
export const humanizeKey = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase → words
    .replace(/_/g, " ")
    .replace(/\bcpt\b/gi, "CPT")
    .replace(/\bid\b/gi, "ID")
    .replace(/^\w/, (c) => c.toUpperCase());

const asNum = (v: unknown): number | null => {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)))
    return Number(v);
  return null;
};

/** Case-insensitive lookup of the first present key among `keys` on a plain object. */
const lookup = (obj: unknown, ...keys: string[]): unknown => {
  if (!obj || typeof obj !== "object") return undefined;
  const lower = keys.map((k) => k.toLowerCase());
  const found = Object.entries(obj as Record<string, unknown>).find(([k]) =>
    lower.includes(k.toLowerCase())
  );
  return found ? found[1] : undefined;
};

/** The one scalar we can compactly compare for a component type ("102%", "0.85× charges"), else null. */
const scalarLabel = (componentType: string, obj: unknown): string | null => {
  if (componentType === "rate_calculation") {
    const pct = asNum(lookup(obj, "ratePercent"));
    return pct != null ? `${pct}%` : null;
  }
  if (componentType === "lesser_of_charges") {
    const m = asNum(lookup(obj, "multiplier", "capMultiplier"));
    return m != null ? `${m}× charges` : null;
  }
  return null;
};

export interface IComparisonSummary {
  proposed: string | null;
  production: string | null;
}

/**
 * A compact "proposed X vs production Y" pair for the checklist's production column — for the value-
 * comparable types (rate-calc %, lesser-of multiplier). Types whose values live elsewhere (carveout CSVs,
 * rulesets we don't value-diff) return nulls; those are judged on the recommendation card below.
 */
export const comparisonSummary = (
  rec: IDraftRecommendation,
  match: IDraftRecMatch | null | undefined
): IComparisonSummary => ({
  proposed: scalarLabel(rec.componentType, rec.proposedModeling),
  production:
    match && match.rows.length > 0
      ? scalarLabel(rec.componentType, match.rows[0])
      : null,
});

const strOf = (v: unknown): string | null =>
  typeof v === "string" && v.trim() !== "" ? v : null;

const pmObj = (rec: IDraftRecommendation): Record<string, unknown> =>
  rec.proposedModeling && typeof rec.proposedModeling === "object"
    ? (rec.proposedModeling as Record<string, unknown>)
    : {};

/** The payer a recommendation is for, from its proposedModeling. Payer-agnostic recs (base files, terms) have none. */
export const recPayer = (
  rec: IDraftRecommendation
): { id: number | null; name: string | null } => {
  const p = pmObj(rec);
  return {
    id: asNum(lookup(p, "payerId")),
    name: strOf(lookup(p, "payerName")),
  };
};

/**
 * The one line that distinguishes otherwise-identical rows of the same type (e.g. three rate calcs) —
 * the rate/file, multiplier, or ruleset group. Payer is shown separately (rows are grouped by payer).
 */
export const recSubtitle = (rec: IDraftRecommendation): string | null => {
  const p = pmObj(rec);
  const file = strOf(lookup(p, "baseRateFileDisplayName", "baseRateFile"));
  const group = strOf(lookup(p, "groupName"));
  if (rec.componentType === "rate_calculation") {
    return (
      [scalarLabel("rate_calculation", p), file].filter(Boolean).join(" · ") ||
      null
    );
  }
  if (rec.componentType === "base_rate_file") return file;
  if (rec.componentType === "lesser_of_charges")
    return scalarLabel("lesser_of_charges", p);
  return group || file || null;
};

/**
 * Distinct base-rate-file names among a match's production rows — used to name the file behind an
 * "exists, not verified" (`present`) verdict, so a name-mismatch reads as "a file exists (HMA Aetna Fac),
 * just not under the proposed name" rather than a vague "exists." Empty for rows that carry no file name
 * (carveouts / rulesets that land on `present` for a different reason).
 */
export const productionFileNames = (
  match: IDraftRecMatch | null | undefined
): string[] => {
  if (!match) return [];
  const names = new Set<string>();
  match.rows.forEach((r) => {
    const n = strOf(lookup(r, "baseRateFile", "displayName"));
    if (n) names.add(n);
  });
  return [...names];
};

const RULESET_EDIT_SEGMENT: Record<string, string> = {
  lesser_of_charges: "lesser-of-charges",
  mpdr_ruleset: "mppr",
  case_rate_ruleset: "case-rate",
  package_ruleset: "packages",
  inpatient_ruleset: "inpatient-rulesets",
  exploding_fee_schedule: "exploding-fee-schedules",
  charge_based_pricing: "charge-based-pricing",
  unit_cap_ruleset: "unit-cap",
};

/** Live-row keys that are link metadata, not values to display. */
export const LIVE_META_KEYS = ["linkEditId", "linkCfsId"];

/**
 * A deep link into the Payer Rules editor for a live production row, or null if that construct has no
 * navigable page. Rulesets link to their own edit page; contract terms / rate calcs / carveouts / base
 * rate files link to the contract fee schedule page they live on (there's no per-item route for those).
 * Relies on the linkEditId / linkCfsId metadata the live-model reader attaches to each row.
 */
export const payerRulesHref = (
  componentType: string,
  row: Record<string, unknown> | null | undefined
): string | null => {
  if (!row) return null;
  const editId = typeof row.linkEditId === "string" ? row.linkEditId : null;
  const cfsId = typeof row.linkCfsId === "string" ? row.linkCfsId : null;
  const seg = RULESET_EDIT_SEGMENT[componentType];
  if (seg && editId) return `/payer-rules/${seg}/edit/${editId}`;
  if (
    cfsId &&
    (componentType === "contract_term" ||
      componentType === "rate_calculation" ||
      componentType === "carveout" ||
      componentType === "base_rate_file")
  )
    return `/payer-rules/contracts/${cfsId}`;
  return null;
};

/** The first deep link among a recommendation's matched production rows, or null. */
export const firstPayerRulesHref = (
  componentType: string,
  rows: Record<string, unknown>[] | null | undefined
): string | null => {
  if (!rows) return null;
  const hit = rows.map((r) => payerRulesHref(componentType, r)).find(Boolean);
  return hit ?? null;
};

/**
 * Can the tool auto-generate this base_rate_file's CSV today? Mirrors the three cases the backend
 * `generateBaseRateFileCsv` actually handles, keyed on `action`:
 *   - `generate` — a Medicare physician fee schedule (facility/non-facility) with a locality GPCI.
 *   - `generate_from_contract` — a service-line rate sheet with at least one priced line.
 *   - `generate_from_existing` — a whole-percent multiple of a loaded file.
 * `reuse_existing` and `upload_required` are not generatable (there's nothing to compute), so no button.
 * The backend is the source of truth; this only decides whether to show the download button.
 */
export const canGenerateBaseRateFile = (rec: IDraftRecommendation): boolean => {
  if (rec.componentType !== "base_rate_file") return false;
  const pm = pmObj(rec);
  const action = String(pm.action ?? "");

  if (action === "generate_from_existing") {
    return (
      typeof pm.sourceDisplayName === "string" &&
      pm.sourceDisplayName.trim() !== "" &&
      (asNum(pm.percentOfSource) ?? 0) > 0
    );
  }

  if (action === "generate_from_contract") {
    // Generatable once it carries at least one line with a name and a rate.
    return (
      Array.isArray(pm.lines) &&
      pm.lines.some((l) => {
        const line = (l ?? {}) as Record<string, unknown>;
        return (
          typeof line.serviceLine === "string" &&
          line.serviceLine.trim() !== "" &&
          asNum(line.rateInCents) != null
        );
      })
    );
  }

  if (action === "generate_percent_by_service_line") {
    // One consolidated file: needs a source file, a catch-all %, and at least one named line with a %.
    return (
      typeof pm.sourceDisplayName === "string" &&
      pm.sourceDisplayName.trim() !== "" &&
      (asNum(pm.catchAllPercent) ?? 0) > 0 &&
      Array.isArray(pm.lines) &&
      pm.lines.some((l) => {
        const line = (l ?? {}) as Record<string, unknown>;
        return (
          typeof line.serviceLine === "string" &&
          line.serviceLine.trim() !== "" &&
          (asNum(line.percent) ?? 0) > 0
        );
      })
    );
  }

  // The Medicare physician case — either action === "generate", or a legacy rec with no action but the
  // physician shape. Requires the fee-schedule type, all three GPCIs and a year.
  const gen = (
    pm.baseRateFileGeneration && typeof pm.baseRateFileGeneration === "object"
      ? pm.baseRateFileGeneration
      : pm
  ) as Record<string, unknown>;
  const feeType = String(gen.feeScheduleType ?? "").toUpperCase();
  if (feeType !== "PHYSICIAN_FACILITY" && feeType !== "PHYSICIAN_NON_FACILITY")
    return false;
  const gpci = (
    gen.gpci && typeof gen.gpci === "object" ? gen.gpci : gen
  ) as Record<string, unknown>;
  const hasGpci = [
    gpci.pw ?? gen.gpciPw,
    gpci.pe ?? gen.gpciPe,
    gpci.mp ?? gen.gpciMp,
  ].every((v) => asNum(v) != null);
  return hasGpci && asNum(gen.year) != null;
};

/** The base_rate_file actions that PRODUCE a downloadable CSV (vs reuse/upload, which don't). */
const GENERATE_ACTIONS = [
  "generate",
  "generate_from_contract",
  "generate_from_existing",
  "generate_percent_by_service_line",
];

/** True when this base rate file is set to a generate-type action — i.e. a file we'd generate & download. */
export const isGeneratableAction = (rec: IDraftRecommendation): boolean =>
  rec.componentType === "base_rate_file" &&
  GENERATE_ACTIONS.includes(String(pmObj(rec).action ?? ""));

/**
 * What's still missing before a generate-type base rate file can be generated — so the reviewer sees WHY
 * the download button is disabled instead of nothing at all. Empty when the file is ready (or not a
 * generate action). Mirrors the field checks in `canGenerateBaseRateFile`.
 */
export const generationBlockers = (rec: IDraftRecommendation): string[] => {
  if (!isGeneratableAction(rec)) return [];
  const pm = pmObj(rec);
  const action = String(pm.action ?? "");
  const hasStr = (k: string): boolean => {
    const v = pm[k];
    return typeof v === "string" && v.trim() !== "";
  };
  const positive = (k: string): boolean => (asNum(pm[k]) ?? 0) > 0;
  const hasNamedLine = (pctKey: "rateInCents" | "percent"): boolean =>
    Array.isArray(pm.lines) &&
    pm.lines.some((l) => {
      const line = (l ?? {}) as Record<string, unknown>;
      const named =
        typeof line.serviceLine === "string" && line.serviceLine.trim() !== "";
      const priced =
        pctKey === "percent"
          ? (asNum(line.percent) ?? 0) > 0
          : asNum(line.rateInCents) != null;
      return named && priced;
    });

  const missing: string[] = [];
  if (action === "generate") {
    if (!hasStr("feeScheduleType")) missing.push("fee schedule type");
    if (asNum(pm.year) == null) missing.push("year");
    const g = (pm.gpci && typeof pm.gpci === "object" ? pm.gpci : {}) as Record<
      string,
      unknown
    >;
    if (![g.pw, g.pe, g.mp].every((v) => asNum(v) != null))
      missing.push("GPCIs (work, practice expense, malpractice)");
  } else if (action === "generate_from_existing") {
    if (!hasStr("sourceDisplayName")) missing.push("a source file");
    if (!positive("percentOfSource")) missing.push("a percent of that file");
  } else if (action === "generate_percent_by_service_line") {
    if (!hasStr("sourceDisplayName")) missing.push("a source file");
    if (!positive("catchAllPercent")) missing.push("a catch-all percent");
    if (!hasNamedLine("percent"))
      missing.push("at least one service line with a percent");
  } else if (action === "generate_from_contract") {
    if (!hasNamedLine("rateInCents"))
      missing.push("at least one service line with a rate");
  }
  return missing;
};

/** One-line description of what a generate-type base rate file will produce (action-aware, not hard-coded to Medicare). */
export const generationHelpText = (rec: IDraftRecommendation): string => {
  switch (String(pmObj(rec).action ?? "")) {
    case "generate":
      return "Generates the Medicare physician fee schedule (CPT → rate) from the locality GPCIs.";
    case "generate_from_existing":
      return "Generates a whole-percent multiple of the loaded source file (CPT → rate).";
    case "generate_percent_by_service_line":
      return "Generates one file that applies a different percent of the source file per service line (CPT → rate).";
    case "generate_from_contract":
      return "Generates a CPT → rate file from the contract's per-service-line rates.";
    default:
      return "";
  }
};

/**
 * The `baseRateFileId` a `reuse_existing` base rate file should carry, when it's missing but the chosen
 * file NAME points at a loaded file — else null (leave it alone). Returns null unless ALL of: the action
 * is reuse_existing, the id is currently empty, and the displayName matches a loaded file.
 *
 * Why this exists: the reuse dropdown is keyed on the file NAME and only writes the id on an active pick.
 * But saving the rec under a different action strips `baseRateFileId` (Zod drops out-of-branch keys), so
 * switching back to "reuse" leaves a name with no id — which the dropdown shows as a valid selection but
 * won't save (`baseRateFileId: Required`). This re-derives the id from the name so it can't silently go
 * missing. Deliberately only fills a MISSING id — it never rewrites a present one (e.g. an older version).
 */
export const reuseBaseRateFileIdToFill = (
  proposedModeling: Record<string, unknown>,
  options: { id: string; displayName: string }[]
): string | null => {
  if (String(proposedModeling.action ?? "") !== "reuse_existing") return null;
  if (proposedModeling.baseRateFileId) return null;
  const name =
    typeof proposedModeling.displayName === "string"
      ? proposedModeling.displayName
      : "";
  if (!name) return null;
  return options.find((f) => f.displayName === name)?.id ?? null;
};

export interface IPayerGroup {
  key: string;
  payerId: number | null;
  payerName: string | null;
  recs: IDraftRecommendation[];
}

/** A base rate file's own key(s) for matching against the rate calcs that reference it. */
const baseFileKeys = (rec: IDraftRecommendation): string[] => {
  const pm = pmObj(rec);
  return [
    strOf(lookup(pm, "baseRateFileId")),
    strOf(lookup(pm, "displayName"))?.toLowerCase() ?? null,
  ].filter((k): k is string => k != null);
};

/**
 * Map each base rate file (by id and by lower-cased name) to the payer of the rate calc that prices off
 * it. A file referenced by MORE THAN ONE payer is genuinely shared, so it maps to `null` (ambiguous) and
 * stays contract-wide. This is what lets a payer-agnostic base rate file be shown under its real payer.
 */
const filePayerFromRateCalcs = (
  recs: IDraftRecommendation[]
): Map<string, { id: number; name: string | null } | null> => {
  const map = new Map<string, { id: number; name: string | null } | null>();
  recs.forEach((r) => {
    if (r.componentType !== "rate_calculation") return;
    const { id, name } = recPayer(r);
    if (id == null) return;
    const pm = pmObj(r);
    const keys = [
      strOf(lookup(pm, "baseRateFileId")),
      strOf(
        lookup(pm, "baseRateFileDisplayName", "baseRateFile")
      )?.toLowerCase() ?? null,
    ].filter((k): k is string => k != null);
    keys.forEach((k) => {
      if (!map.has(k)) map.set(k, { id, name });
      else {
        const prev = map.get(k);
        if (prev && prev.id !== id) map.set(k, null); // referenced by two payers → ambiguous
      }
    });
  });
  return map;
};

/**
 * Group recommendations by the payer they're for — a single contract often binds several payers (an HMA
 * network schedule honored by Aetna, Aetna Medicare Advantage, First Health), so grouping is what turns
 * "three identical rate calcs" into "one per payer". A base rate file carries no payer of its own, so it's
 * placed under the payer of the rate calc that references it (via `context`, which defaults to `recs` but
 * is the FULL rec set when `recs` is a filtered subset like one wizard step); a file shared by two payers,
 * or referenced by none, falls back to a "Contract-wide" group shown first. Payer groups follow in
 * first-appearance order; recs within a group are in build order.
 */
export const groupByPayer = (
  recs: IDraftRecommendation[],
  context: IDraftRecommendation[] = recs
): IPayerGroup[] => {
  const filePayer = filePayerFromRateCalcs(context);
  const groups = new Map<string, IPayerGroup>();
  const order: string[] = [];
  recs.forEach((r) => {
    let { id, name } = recPayer(r);
    // A base rate file inherits the payer of the rate calc that prices off it (single-payer files only).
    if (id == null && r.componentType === "base_rate_file") {
      const hit = baseFileKeys(r)
        .map((k) => filePayer.get(k))
        .find((v) => v); // first resolved (non-ambiguous, present) payer
      if (hit) {
        id = hit.id;
        name = hit.name;
      }
    }
    const key = id != null ? `p${id}` : "contract-wide";
    if (!groups.has(key)) {
      groups.set(key, { key, payerId: id, payerName: name, recs: [] });
      order.push(key);
    }
    const g = groups.get(key);
    if (g) g.recs.push(r);
  });
  const ordered = order.map((k) => groups.get(k) as IPayerGroup);
  const contractWide = ordered.filter((g) => g.key === "contract-wide");
  const payerGroups = ordered.filter((g) => g.key !== "contract-wide");
  return [...contractWide, ...payerGroups].map((g) => ({
    ...g,
    recs: forChecklist(g.recs),
  }));
};

/** Human label for a recommendation's review status. */
export const recStatusLabel = (status: string): string => {
  switch (status) {
    case "pending":
      return "Needs review";
    case "accepted":
      return "Accepted";
    case "overridden":
      return "Feedback given";
    case "rejected":
      return "Rejected";
    case "needs_input":
      return "Blocked";
    case "created":
      return "Created";
    case "superseded":
      return "Superseded";
    default:
      return status;
  }
};

/**
 * How to implement each recommendation type in the product UI — the "to-do" instruction shown on the
 * implementation checklist. Precise enough to act on; the values come from the proposal itself.
 */
const IMPLEMENTATION_INSTRUCTIONS: Record<string, string> = {
  contract_term:
    "On the Payer Rules page, create the Contract Fee Schedule for this provider × payer with the effective date shown (and any future-dated escalator terms).",
  base_rate_file:
    "Make sure the base rate file below is loaded (reuse the named one, or upload the generated CSV), then reference it from the rate calculation.",
  rate_calculation:
    "Add a Contract Rate Calculation to the contract fee schedule: the base rate file and rate percent shown, at the given sort order.",
  carveout:
    "Create a Contract Carveout for the listed codes/amounts (upload the CSV of CPT → fixed dollar).",
  lesser_of_charges:
    "Create a Lesser-of-Charges ruleset for this provider × payer at the multiplier shown.",
  charge_based_pricing:
    "Create a Charge-Based Pricing ruleset for the catch-all percentage-of-charges shown.",
  mpdr_ruleset:
    "Create a Multiple-Procedure Discount ruleset with the tier percentages shown.",
  case_rate_ruleset: "Create a Case-Rate ruleset for the bundle/group shown.",
  inpatient_ruleset:
    "Create an Inpatient ruleset (DRG base, weights, outlier/per-diem terms) as shown.",
  package_ruleset: "Create a Package ruleset for the grouping shown.",
  exploding_fee_schedule:
    "Create an Exploding Fee Schedule ruleset (percentage applied to the base rate file shown).",
  ma_payer_link:
    "This is a routing decision, not a single construct: if a separate Medicare Advantage payer is needed, create it first, then give it its own Contract Fee Schedule. Nothing here is created automatically.",
  provider_decomposition: `Scoping decision — confirm the provider entities involved; model each as its own ${CONTRACT_MODEL}.`,
  payer_consolidation:
    "Scoping decision — confirm which payers consolidate into this treatment.",
};

export const implementationInstruction = (componentType: string): string =>
  IMPLEMENTATION_INSTRUCTIONS[componentType] ??
  `Create the ${componentTypeLabel(componentType)} construct as shown.`;

// ---------------------------------------------------------------------------
// Concrete, value-filled build steps (Build & Verify)
// ---------------------------------------------------------------------------

const money = (cents: unknown): string => {
  const n = asNum(cents);
  return n == null
    ? "?"
    : `$${(n / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
};

const str = (v: unknown): string | null =>
  typeof v === "string" && v.trim() !== "" ? v.trim() : null;

const codeList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((c): c is string => typeof c === "string") : [];

/** "97110, 97112 and 3 more" — a code list you can eyeball, with an exact remainder. */
const codesPhrase = (codes: string[]): string => {
  if (codes.length === 0) return "the codes shown";
  const shown = codes.slice(0, 6).join(", ");
  const rest = codes.length - Math.min(6, codes.length);
  return rest > 0 ? `${shown} and ${rest} more` : shown;
};

/**
 * The actual, field-by-field steps to build one recommendation in Payer Rules — with the proposal's
 * own values filled in. This replaces the one-line generic instruction on the Build checklist, which
 * told you *what* to make but not *what to type*.
 *
 * Falls back to the generic sentence for types with no bespoke recipe, so a new componentType is never
 * blank. Ruleset descriptions reuse `describeRuleLogic` so the wording matches the Adjustment Rules step.
 */
export const buildSteps = (
  rec: IDraftRecommendation,
  opts: { payerName?: string | null; providerName?: string | null } = {}
): string[] => {
  const pm = pmObj(rec);
  const who = `${
    opts.providerName ?? str(pm.providerName) ?? "this provider"
  } × ${opts.payerName ?? str(pm.payerName) ?? "this payer"}`;

  switch (rec.componentType) {
    case "contract_term": {
      const steps = [
        "Payer Rules → Contract Fee Schedules → New.",
        `Provider and payer: ${who}.`,
        `Effective date: ${str(pm.effectiveDate) ?? "as shown"}${
          str(pm.endDate) ? `, end date ${str(pm.endDate)}` : ""
        }.`,
      ];
      const esc = (pm.escalator ?? null) as Record<string, unknown> | null;
      const pct = esc ? asNum(esc.percentPerYear) : null;
      if (pct != null) {
        steps.push(
          `Annual increase of ${pct}%: don't enter it here — create a future-dated fee schedule effective ${
            str(esc?.firstIncreaseOn) ??
            str(pm.nextUpdateDate) ??
            "the next update date"
          } with the stepped rate.`
        );
      } else if (str(pm.nextUpdateDate)) {
        steps.push(`Note the next update date: ${str(pm.nextUpdateDate)}.`);
      }
      return steps;
    }

    case "base_rate_file": {
      const name = str(pm.displayName) ?? "the file shown";
      const action = str(pm.action);
      if (action === "reuse_existing") {
        return [
          `Confirm the base rate file "${name}" is already loaded (Payer Rules → Base Rate Files).`,
          "You'll reference it from the rate calculation — nothing to create here.",
        ];
      }
      if (action === "upload_required") {
        // The model's `reason` is often a long paragraph (how to split/flatten/expand the file). It is
        // rendered SEPARATELY as a note — jamming it into "step 1" turned the step into a wall of text.
        return [
          `Obtain and prepare the base rate file "${name}"${
            str(pm.reason) ? " — see the note below for how" : ""
          }.`,
          "Payer Rules → Base Rate Files → Upload, then reference it from the rate calc.",
        ];
      }
      // Generatable actions (generate / from_contract / from_existing).
      return [
        'Click "Generate & download base rate CSV" on this recommendation.',
        `Payer Rules → Base Rate Files → Upload the CSV, named "${name}".`,
        "Reference it from the rate calculation.",
      ];
    }

    case "rate_calculation": {
      const rateType = str(pm.rateType);
      const rate =
        rateType === "unit"
          ? `${money(pm.ratePercent)} per unit`
          : `${asNum(pm.ratePercent) ?? "?"}% of the base rate file`;
      return [
        `Open the Contract Fee Schedule for ${who}.`,
        "Add a Rate Calculation.",
        // Cross-reference so it's clear this isn't a separate file to hunt down — it's the one built in
        // the Base Rate File step above.
        `Base rate file: ${
          str(pm.baseRateFileDisplayName) ?? "as shown"
        } (the base rate file from the step above).`,
        `Rate: ${rate}.`,
        `Sort order: ${asNum(pm.sortOrder) ?? 0}.`,
      ];
    }

    case "carveout": {
      const rows = Array.isArray(pm.rows)
        ? (pm.rows as Record<string, unknown>[])
        : [];
      const steps = [
        `Open the Contract Fee Schedule for ${who}.`,
        `Add a Carveout referencing "${
          str(pm.baseRateFileDisplayName) ?? "the file shown"
        }".`,
      ];
      if (rows.length > 0) {
        steps.push(
          `Codes and amounts: ${rows
            .slice(0, 8)
            .map((r) => `${str(r.code) ?? "?"} → ${money(r.amountInCents)}`)
            .join("; ")}${
            rows.length > 8 ? `; and ${rows.length - 8} more` : ""
          }.`
        );
      }
      return steps;
    }

    case "lesser_of_charges":
    case "charge_based_pricing":
    case "mpdr_ruleset":
    case "case_rate_ruleset":
    case "package_ruleset":
    case "exploding_fee_schedule":
    case "unit_cap_ruleset": {
      const label = componentTypeLabel(rec.componentType);
      return [
        `Payer Rules → ${label} → New, for ${who}.`,
        `Rule: ${describeRuleLogic(rec)}`,
        `Applies to: ${ruleAppliesTo(rec)}.`,
        str(pm.groupName) ? `Group name: ${str(pm.groupName)}.` : null,
      ].filter((s): s is string => s !== null);
    }

    case "inpatient_ruleset": {
      const config = (pm.config ?? {}) as Record<string, unknown>;
      return [
        `Payer Rules → Inpatient Rulesets → New, for ${who}.`,
        `Name: ${str(pm.name) ?? "as shown"}.`,
        `DRG codes: ${codesPhrase(codeList(pm.drgCodes))}.`,
        `Type: ${
          str(config.type) ?? "as shown"
        } — enter its terms from the fields.`,
      ];
    }

    default:
      return [implementationInstruction(rec.componentType)];
  }
};

/**
 * The model's longer explanation for a checklist item, shown as a secondary note UNDER the steps rather
 * than crammed into a numbered step. Today that's the `reason` on a base_rate_file the model couldn't
 * auto-build — a paragraph on how to split/flatten/expand the source file. Returns null when there's
 * nothing worth a note, or when it's short enough to have stayed inline.
 */
export const buildStepsNote = (rec: IDraftRecommendation): string | null => {
  const pm = pmObj(rec);
  const reason = str(pm.reason);
  if (rec.componentType === "base_rate_file" && reason && reason.length > 120) {
    return reason;
  }
  return null;
};

/** Rejected recommendations don't belong on the to-do list. */
export const isOnChecklist = (rec: IDraftRecommendation): boolean =>
  rec.status !== "rejected";
